import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Wallet } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export function PortfolioSimulator() {
  const { t } = useTranslation();
  const [capital, setCapital] = useState(100000);
  const [days, setDays] = useState(365);
  const [risk, setRisk] = useState('medium');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const simulate = async () => {
    setLoading(true);
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/portfolio/simulate`,
        { initial_capital: capital, duration_days: days, risk_level: risk },
        { withCredentials: true }
      );
      setResult(data);
    } catch (err) {
      console.error('Simulation error:', err);
    }
    setLoading(false);
  };

  return (
    <div className="bg-[#090B0F] border border-white/10 p-4" data-testid="portfolio-simulator">
      <div className="flex items-center gap-2 mb-4">
        <Wallet className="w-5 h-5 text-emerald-500" />
        <h3 className="text-sm uppercase tracking-[0.2em] font-semibold text-white">{t('dashboard.simulator')}</h3>
      </div>
      <div className="space-y-3">
        <div>
          <label className="text-xs text-slate-400 mb-1 block">{t('dashboard.initialCapital')}</label>
          <input type="number" value={capital} onChange={(e) => setCapital(Number(e.target.value))}
            className="w-full bg-[#11141D] border border-white/10 text-white px-3 py-2 font-mono text-sm focus:border-white/25 outline-none"
            data-testid="simulator-capital-input" />
        </div>
        <div>
          <label className="text-xs text-slate-400 mb-1 block">{t('dashboard.durationDays')}</label>
          <input type="number" value={days} onChange={(e) => setDays(Number(e.target.value))}
            className="w-full bg-[#11141D] border border-white/10 text-white px-3 py-2 font-mono text-sm focus:border-white/25 outline-none"
            data-testid="simulator-days-input" />
        </div>
        <div>
          <label className="text-xs text-slate-400 mb-1 block">{t('dashboard.riskLevel')}</label>
          <select value={risk} onChange={(e) => setRisk(e.target.value)}
            className="w-full bg-[#11141D] border border-white/10 text-white px-3 py-2 font-mono text-sm focus:border-white/25 outline-none"
            data-testid="simulator-risk-select">
            <option value="low">{t('dashboard.riskLow')}</option>
            <option value="medium">{t('dashboard.riskMedium')}</option>
            <option value="high">{t('dashboard.riskHigh')}</option>
          </select>
        </div>
        <button onClick={simulate} disabled={loading}
          className="w-full bg-emerald-500 text-black py-2 font-bold text-sm hover:bg-emerald-400 transition-colors disabled:opacity-50"
          data-testid="portfolio-simulator-button">
          {loading ? 'SIMULATING...' : 'SIMULATE'}
        </button>
        {result && (
          <div className="bg-[#11141D] p-3 space-y-2" data-testid="simulation-result">
            <div className="flex justify-between">
              <span className="text-slate-400 text-xs">{t('dashboard.finalValue')}</span>
              <span className="font-mono text-white">${result.final_value?.toLocaleString()}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-400 text-xs">{t('dashboard.totalReturn')}</span>
              <span className={`font-mono ${result.total_return >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                {result.total_return >= 0 ? '+' : ''}{result.total_return}%
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
